import { useState } from 'react';
import { apiService } from '../utils/api';
import { useTheme } from '../hooks/useTheme';
import logoDark from '../assets/logo-repro-v3-dark.svg';
import logoWhite from '../assets/logo-repro-white.svg';

const ExpiredLink = () => {
  const { resolvedTheme } = useTheme();
  const logoSvg = resolvedTheme === 'dark' ? logoWhite : logoDark;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const result = await apiService.sendMagicLink(email);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message || 'Failed to send link. Please try again.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src={logoSvg}
            alt="Repro"
            className="h-6 w-auto mx-auto mb-6"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="mx-auto h-12 w-12 text-warning mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-base-content">
            This Link Has Expired
          </h2>
          <p className="mt-2 text-sm text-base-content/70">
            Magic links expire after 20 minutes for security. Enter your email
            below to receive a new one.
          </p>
        </div>

        {success ? (
          <div className="alert alert-success" role="alert" aria-live="polite">
            <div>
              <p className="font-medium">Check your email!</p>
              <p className="mt-1 text-sm">
                We&apos;ve sent a new magic link to <strong>{email}</strong>. It
                will expire in 20 minutes.
              </p>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleResend}>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="input input-bordered w-full"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !email}
                className="btn btn-primary w-full"
              >
                {loading ? 'Sending...' : 'Send New Link'}
              </button>
            </div>

            {error && (
              <div
                className="alert alert-error"
                role="alert"
                aria-live="polite"
              >
                <span>{error}</span>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ExpiredLink;
